import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import CustomToggleSwitch from "../CustomToggleSwitch";
import { selectAccountData } from "../../redux/main/accountSlice";
import { swapModal, setHasToRefresh } from "../../redux/rightModalSlice";
import { getNodeApiUrl } from "../../config/api.config";

export default function QuickAddProspect() {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token') ?? '';

    const [isBusiness, setIsBusiness] = useState(true);
    const [formData, setFormData] = useState({
        company: '',
        name: '',
        surname: '',
        phone: '',
        email: '',
        address: '',
        notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validazione
        if (isBusiness && !formData.company.trim()) {
            setError('La ragione sociale è obbligatoria');
            return;
        }
        if (!isBusiness && !formData.name.trim() && !formData.surname.trim()) {
            setError('Nome o cognome è obbligatorio');
            return;
        }

        setSaving(true);

        // Costruisci il payload
        const customer = {
            accountId: accountData?._id,
            prospect: true,
            business: isBusiness,
            contactsList: []
        };

        if (isBusiness) {
            customer.company = formData.company.trim();
        } else {
            customer.name = formData.name.trim();
            customer.surname = formData.surname.trim();
        }

        // Indirizzo minimo
        if (formData.address.trim()) {
            customer.addresses = [{
                address: formData.address.trim()
            }];
        }

        // Contatto con email (senza accesso area riservata)
        if (formData.email.trim() || formData.phone.trim()) {
            customer.contactsList.push({
                name: isBusiness ? '' : formData.name.trim(),
                surname: isBusiness ? '' : formData.surname.trim(),
                phone: formData.phone.trim(),
                email: formData.email.trim(),
                enabledToReservedArea: false
            });
        }

        // Note
        if (formData.notes.trim()) {
            customer.notes = formData.notes.trim();
        }

        try {
            const url = getNodeApiUrl('/customers');
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ customer })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Errore nella creazione del prospect');
            }

            const resultData = await response.json();
            dispatch(setHasToRefresh());

            // Torna alla lead con il prospect appena creato
            dispatch(swapModal({
                createdProspect: resultData
            }));
        } catch (err) {
            console.error('Errore creazione prospect:', err);
            setError(err.message || 'Errore nella creazione del prospect');
        } finally {
            setSaving(false);
        }
    };

    const handleGoBack = () => {
        dispatch(swapModal());
    };

    const inputClass = "w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";

    return (
        <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
                <button
                    type="button"
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={handleGoBack}
                    title="Torna alla lead"
                >
                    <DynamicIcon iconClass="fas fa-arrow-left" />
                </button>
                <h2 className="text-2xl font-bold">Nuovo Prospect</h2>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Toggle Business/Privato */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm ${!isBusiness ? 'font-semibold' : 'text-gray-500'}`}>Privato</span>
                        <CustomToggleSwitch
                            value={isBusiness}
                            onChange={(val) => setIsBusiness(val)}
                        />
                        <span className={`text-sm ${isBusiness ? 'font-semibold' : 'text-gray-500'}`}>Business</span>
                    </div>
                </div>

                {/* Campi anagrafici */}
                {isBusiness ? (
                    <div className="mb-4">
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                            Ragione Sociale *
                        </label>
                        <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Ragione sociale..."
                        />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Nome *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={inputClass}
                                placeholder="Nome..."
                            />
                        </div>
                        <div>
                            <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-1">
                                Cognome *
                            </label>
                            <input
                                type="text"
                                id="surname"
                                name="surname"
                                value={formData.surname}
                                onChange={handleInputChange}
                                className={inputClass}
                                placeholder="Cognome..."
                            />
                        </div>
                    </div>
                )}

                {/* Contatti */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Telefono
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Telefono..."
                        />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={inputClass}
                            placeholder="Email..."
                        />
                    </div>
                </div>

                {/* Indirizzo */}
                <div className="mb-4">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Indirizzo
                    </label>
                    <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="Indirizzo..."
                    />
                </div>

                {/* Note */}
                <div className="mb-4">
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                        Note
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="Note..."
                        rows="3"
                    />
                </div>

                {/* Errore */}
                {error && (
                    <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                        <p>{error}</p>
                    </div>
                )}

                {/* Bottoni */}
                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        type="button"
                        className="secondary"
                        onClick={handleGoBack}
                        disabled={saving}
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        className="primary"
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                Crea Prospect
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
